"use client";

import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import { useConvexAuth } from "convex/react";

const OAuthCodeContext = createContext(false);
export const useIsExchangingOAuthCode = () => useContext(OAuthCodeContext);

const RETRY_DELAY_MS = 1200;
const MAX_RETRIES = 2;

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function OAuthCodeHandler({ children }: { children: ReactNode }) {
  const { signIn } = useAuthActions();
  const { isAuthenticated } = useConvexAuth();
  const [isExchanging, setIsExchanging] = useState(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).has("code");
  });
  const handled = useRef(false);

  useEffect(() => {
    if (isAuthenticated) {
      const url = new URL(window.location.href);
      if (url.searchParams.has("code")) {
        url.searchParams.delete("code");
        window.history.replaceState(null, "", url.pathname + url.search + url.hash);
      }
      const timeout = window.setTimeout(() => setIsExchanging(false), 0);
      return () => window.clearTimeout(timeout);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("code");
    if (!code || handled.current || isAuthenticated) return;
    handled.current = true;

    void (async () => {
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
        try {
          await signIn("google", { code });
          const url = new URL(window.location.href);
          url.searchParams.delete("code");
          window.location.replace(url.pathname + url.search + url.hash);
          return;
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          const isConnectionLoss = message.includes("Connection lost while action was in flight");

          if (!isConnectionLoss || attempt === MAX_RETRIES) {
            console.error("OAuth code exchange failed", error);
            setIsExchanging(false);
            return;
          }

          await sleep(RETRY_DELAY_MS);
          if (isAuthenticated) {
            return;
          }
        }
      }
    })();
  }, [isAuthenticated, signIn]);

  return (
    <OAuthCodeContext.Provider value={isExchanging}>
      {children}
    </OAuthCodeContext.Provider>
  );
}
